import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styling/loanlist.css";
import LoadingMask from "./LoadingMask";
import FlashMessage from "../components/FlashMessage";
import moment from "moment";
import { FaPlus, FaEye, FaEyeSlash, FaEdit, FaTrash, FaFilePdf, FaCamera, FaToggleOn, FaToggleOff, FaCalculator, FaPhone } from "react-icons/fa";
import API from "../utils/api";
import TransactionHistory from "./TransactionHistory";
import ConfirmDialog from "./ConfirmationBox";
import { exportLoansPDF } from "../utils/exportPDF";
import { exportScreenshot } from "../utils/exportScreenshot";
import InterestCalculator from "./InterestCalculator";

const LoanList = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [loans, setLoans] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editLoanId, setEditLoanId] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shotLoading, setShotLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState("");
  const [flashType, setFlashType] = useState("success");
  const [showActions, setShowActions] = useState(false);
  const [confirmData, setConfirmData] = useState({ open: false, loanId: null });
  const [statusConfirm, setStatusConfirm] = useState({ open: false, loan: null });
  const [activeTab, setActiveTab] = useState("Active");
  const [showCalc, setShowCalc] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", principal: "", rate: "", date: "" });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");
    if (!storedUser || !storedToken) {
      navigate("/login", { replace: true });
    } else {
      try { setUser(JSON.parse(storedUser)); }
      catch { localStorage.clear(); navigate("/login", { replace: true }); }
    }
  }, [navigate]);

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        setLoading(true);
        const { data } = await API.get("/loans");
        setLoans(data);
      } catch (err) {
        console.error("Error fetching loans:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLoans();
  }, []);

  if (user === undefined) return null;

  // ── Interest calculation — uses inactiveDate as end date if frozen ────────
  function calculateInterest(principal, annualRate, startDate, inactiveDate = null) {
    const endDate = inactiveDate ? new Date(inactiveDate) : new Date();
    const start = new Date(startDate);
    const end = endDate;
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let totalMonths = years * 12 + months;
    const startDay = start.getDate();
    const endDay = end.getDate();
    if (endDay >= startDay) {
      const daysInEnd = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
      totalMonths += (endDay - startDay) / daysInEnd;
    } else {
      totalMonths -= 1;
      const daysInPrev = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
      totalMonths += (daysInPrev - startDay + endDay) / daysInPrev;
    }
    return Number(((principal * annualRate * totalMonths) / 100).toFixed(2));
  }

  // ── Toggle Active / Inactive ──────────────────────────────────────────────
  const handleToggleStatus = async (loan) => {
    const newStatus = loan.status === "Active" ? "Inactive" : "Active";
    try {
      setLoading(true);
      const { data } = await API.put(`/loans/${loan._id}`, { status: newStatus });
      setLoans(loans.map(l => l._id === loan._id ? { ...l, ...data } : l));
      setFlashMessage(`Loan marked as ${newStatus}`);
      setFlashType("success");
    } catch (err) {
      setFlashMessage("Failed to update status");
      setFlashType("error");
    } finally {
      setLoading(false);
      setStatusConfirm({ open: false, loan: null });
    }
  };

  const handleAddOrEditLoan = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const mappedData = {
        borrowerName: form.name,
        phone: form.phone,
        amount: form.principal,
        interestRate: form.rate,
        duration: form.date,
        status: "Active",
        userId: user.id,
      };
      if (editLoanId) {
        const { data } = await API.put(`/loans/${editLoanId}`, mappedData);
        setLoans(loans.map((loan) => (loan._id === editLoanId ? data : loan)));
        setFlashMessage("Loan updated successfully");
        setFlashType("success");
      } else {
        const { data } = await API.post("/loans", mappedData);
        setLoans([data, ...loans]);
        setFlashMessage("Loan created successfully");
        setFlashType("success");
      }
      resetForm();
    } catch (err) {
      setFlashMessage("Failed to save loan");
      setFlashType("error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", phone: "", principal: "", rate: "", date: "" });
    setEditLoanId(null);
    setShowAddModal(false);
  };

  const handleEditLoan = (loan) => {
    setForm({
      name: loan.borrowerName,
      phone: loan.phone || "",
      principal: loan.amount,
      rate: loan.interestRate,
      date: loan.duration ? new Date(loan.duration).toISOString().split("T")[0] : "",
    });
    setEditLoanId(loan._id);
    setShowAddModal(true);
  };

  const handleDeleteLoan = async (loanId) => {
    try {
      setLoading(true);
      await API.delete(`/loans/${loanId}`);
      setLoans(loans.filter((l) => l._id !== loanId));
      setFlashMessage("Loan deleted successfully");
      setFlashType("success");
    } catch (err) {
      setFlashMessage("Failed to delete loan");
      setFlashType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewLoan = (loan) => {
    setSelectedLoan(loan);
    setShowViewModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowViewModal(false);
    setEditLoanId(null);
    setSelectedLoan(null);
    resetForm();
  };

  // ── Export handlers ────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (filteredLoans.length === 0) { setFlashMessage("No loans to export"); setFlashType("error"); return; }
    try {
      setPdfLoading(true);
      await exportLoansPDF(filteredLoans, user?.name || "Admin");
      setFlashMessage("PDF exported successfully!");
      setFlashType("success");
    } catch (err) {
      setFlashMessage("Failed to generate PDF");
      setFlashType("error");
    } finally { setPdfLoading(false); }
  };

  const handleExportScreenshot = async () => {
    if (filteredLoans.length === 0) { setFlashMessage("No loans to capture"); setFlashType("error"); return; }
    try {
      setShotLoading(true);
      await exportScreenshot(user?.name || "Admin");
      setFlashMessage("Screenshot saved!");
      setFlashType("success");
    } catch (err) {
      setFlashMessage("Failed to capture screenshot");
      setFlashType("error");
    } finally { setShotLoading(false); }
  };

  const allActive   = loans.filter(l => l.status === "Active");
  const allInactive = loans.filter(l => l.status === "Inactive");

  // Tab-scoped + search filter
  const tabLoans = loans.filter(l => l.status === activeTab);
  const filteredLoans = tabLoans.filter(l =>
    l.borrowerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Totals: based on SEARCH RESULT of active tab only ──────────────────
  // Active/Inactive counts always show full numbers (unaffected by search)
  // Money stacks reflect whatever is currently visible (search-aware)
  const visibleActive = filteredLoans.filter(l => l.status === "Active");

  const totalPrincipal = visibleActive.reduce((sum, l) => sum + Number(l.amount || 0), 0);
  const totalInterest  = visibleActive.reduce(
    (sum, l) => sum + calculateInterest(l.amount, l.interestRate, l.duration, null), 0
  );
  const totalOutstanding = visibleActive.reduce((sum, l) => {
    const int = calculateInterest(l.amount, l.interestRate, l.duration, null);
    return sum + (Number(l.amount) + int - (l.totalCredit || 0) + (l.totalDebit || 0));
  }, 0);

  // Show "filtered" label when search is active
  const isFiltered = searchTerm.trim().length > 0;

  const calculateDateDiff = (actual_date) => {
    let today = moment();
    let givenDate = moment(actual_date);
    let years = today.diff(givenDate, "years");
    givenDate.add(years, "years");
    let months = today.diff(givenDate, "months");
    givenDate.add(months, "months");
    let days = today.diff(givenDate, "days");
    return `${years}Y ${months}M ${days}D`;
  };

  return (
    <div className="loan-container">
      <FlashMessage message={flashMessage} type={flashType} duration={3000} onClose={() => setFlashMessage("")} />
      {loading && <LoadingMask message="Please wait..." />}

      {/* TOP BAR */}
      <div className="loan-top-bar">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by name..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button type="button" className="clear-search-btn" onClick={() => setSearchTerm("")} aria-label="Clear">✕</button>
          )}
        </div>

        <button className={`btn btn-secondary btn-sm ${showActions ? "active" : ""}`} onClick={() => setShowActions(p => !p)}>
          {showActions ? <><FaEyeSlash /><span> Hide Actions</span></> : <><FaEye /><span> Actions</span></>}
        </button>

        <button className="btn btn-calculator" onClick={() => setShowCalc(true)} title="Interest Calculator">
          <FaCalculator /><span> Calculator</span>
        </button>

        <button className="btn btn-export" onClick={handleExportPDF} disabled={pdfLoading || filteredLoans.length === 0} title="Export PDF">
          <FaFilePdf /><span>{pdfLoading ? " Generating..." : " Export PDF"}</span>
        </button>

        <button className="btn btn-screenshot" onClick={handleExportScreenshot} disabled={shotLoading || filteredLoans.length === 0} title="Screenshot">
          <FaCamera /><span>{shotLoading ? " Capturing..." : " Screenshot"}</span>
        </button>

        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          <FaPlus /> New Loan
        </button>
      </div>

      {/* ── SUMMARY CARDS — active loans only, search-aware ── */}
      <div className="dashboard-summary">
        <div className="summary-card">
          <h4>Total Invested</h4>
          <p>₹{totalPrincipal.toFixed(2)}</p>
          <span className="summary-note">{isFiltered ? "Filtered results" : "Active only"}</span>
        </div>
        <div className="summary-card">
          <h4>Total Interest</h4>
          <p>₹{totalInterest.toFixed(2)}</p>
          <span className="summary-note">{isFiltered ? "Filtered results" : "Active only"}</span>
        </div>
        <div className="summary-card">
          <h4>Total Outstanding</h4>
          <p>₹{totalOutstanding.toFixed(2)}</p>
          <span className="summary-note">{isFiltered ? "Filtered results" : "Active only"}</span>
        </div>
        <div className="summary-card summary-card--active" onClick={() => setActiveTab("Active")} style={{ cursor: "pointer" }}>
          <h4>Active Loans</h4>
          <p>
            <span className="status-dot status-dot--active"></span>
            {allActive.length}
          </p>
          <span className="summary-note">All records</span>
        </div>
        <div className="summary-card summary-card--inactive" onClick={() => setActiveTab("Inactive")} style={{ cursor: "pointer" }}>
          <h4>Inactive Loans</h4>
          <p>
            <span className="status-dot status-dot--inactive"></span>
            {allInactive.length}
          </p>
          <span className="summary-note">All records</span>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="loan-tabs">
        <button
          className={`loan-tab ${activeTab === "Active" ? "loan-tab--active" : ""}`}
          onClick={() => { setActiveTab("Active"); setSearchTerm(""); }}
        >
          <span className="tab-dot tab-dot--active"></span>
          Active
          <span className="tab-count">{allActive.length}</span>
        </button>
        <button
          className={`loan-tab ${activeTab === "Inactive" ? "loan-tab--active" : ""}`}
          onClick={() => { setActiveTab("Inactive"); setSearchTerm(""); }}
        >
          <span className="tab-dot tab-dot--inactive"></span>
          Inactive
          <span className="tab-count tab-count--inactive">{allInactive.length}</span>
        </button>

        {activeTab === "Inactive" && (
          <p className="inactive-notice">
            🔒 Inactive records are frozen — they do not affect totals or accrue interest.
          </p>
        )}
      </div>

      {/* ── LOAN GRID ── */}
      <div className="loan-grid">
        {filteredLoans.length === 0 && !loading && (
          <div className="empty-state">
            <h3>No {activeTab.toLowerCase()} loans found</h3>
            <p>{searchTerm ? "Try adjusting your search" : activeTab === "Active" ? "Add your first loan to get started" : "No inactive loans yet"}</p>
          </div>
        )}

        {filteredLoans.map((loan) => {
          const isInactive = loan.status === "Inactive";
          const interest = calculateInterest(loan.amount, loan.interestRate, loan.duration, loan.inactiveDate);
          const totalDue = (Number(loan.amount) + interest - (loan.totalCredit || 0) + (loan.totalDebit || 0)).toFixed(2);

          // ── Danger zone: total due > 150% of principal ─────────────────
          const principal   = Number(loan.amount);
          const totalDueNum = Number(totalDue);
          const isDanger    = !isInactive && totalDueNum > principal * 1.5;

          return (
            <div
              key={loan._id}
              className={`loan-card ${isInactive ? "loan-card--inactive" : ""} ${isDanger ? "loan-card--danger" : ""}`}
            >
              {/* Danger banner */}
              {isDanger && (
                <div className="danger-banner">
                  ⚠ Follow-up Required — Total due exceeds 150% of principal
                </div>
              )}

              {/* Card header with status indicator + call button */}
              <div className="loan-card-header">
                <h3>{loan.borrowerName}</h3>
                <div className="loan-header-right">
                  {/* Call button — always visible if phone exists */}
                  {loan.phone && (
                    <a
                      href={`tel:${loan.phone}`}
                      className="call-btn"
                      title={`Call ${loan.borrowerName}: ${loan.phone}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaPhone />
                    </a>
                  )}
                  <div className="loan-status-row">
                    <span
                      className={`status-indicator ${isInactive ? "status-indicator--inactive" : "status-indicator--active"}`}
                      title={isInactive ? "Inactive" : "Active"}
                    />
                    <span className={`status-label ${isInactive ? "status-label--inactive" : "status-label--active"}`}>
                      {loan.status}
                    </span>
                  </div>
                </div>
              </div>

              <table className="loan-details-table">
                <tbody>
                  <tr><td><strong>Phone</strong></td><td>
                    {loan.phone
                      ? <a href={`tel:${loan.phone}`} className="phone-link">{loan.phone}</a>
                      : "—"}
                  </td></tr>
                  <tr><td><strong>Principal</strong></td><td>₹{Number(loan.amount).toLocaleString("en-IN")}</td></tr>
                  <tr><td><strong>Rate</strong></td><td>{loan.interestRate}% p.a.</td></tr>
                  <tr><td><strong>Start Date</strong></td><td>{moment(loan.duration).format("DD MMM YYYY")}</td></tr>
                  <tr><td><strong>Duration</strong></td><td>{calculateDateDiff(loan.duration)}</td></tr>
                  <tr>
                    <td><strong>Interest</strong></td>
                    <td>
                      ₹{interest.toFixed(2)}
                      {isInactive && (
                        <span className="frozen-badge" title={`Frozen on ${moment(loan.inactiveDate).format("DD MMM YYYY")}`}>
                          🔒 Frozen
                        </span>
                      )}
                    </td>
                  </tr>
                  {isInactive && loan.inactiveDate && (
                    <tr>
                      <td><strong>Frozen On</strong></td>
                      <td style={{ color: "var(--text-muted)", fontSize: "12px" }}>
                        {moment(loan.inactiveDate).format("DD MMM YYYY")}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td><strong>Total Due</strong></td>
                    <td style={{ color: isInactive ? "var(--text-muted)" : "var(--gold-light)", fontWeight: 600 }}>₹{totalDue}</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: "var(--accent-red)" }}>Credited</strong></td>
                    <td style={{ color: "var(--accent-red)" }}>₹{loan.totalCredit || 0}</td>
                  </tr>
                  <tr>
                    <td><strong style={{ color: "var(--accent-green)" }}>Debited</strong></td>
                    <td style={{ color: "var(--accent-green)" }}>₹{loan.totalDebit || 0}</td>
                  </tr>
                </tbody>
              </table>

              {showActions && (
                <div className="loan-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleViewLoan(loan)}>
                    <FaEye /> View
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEditLoan(loan)}>
                    <FaEdit /> Edit
                  </button>
                  {/* Toggle Active/Inactive */}
                  <button
                    className={`btn btn-sm ${isInactive ? "btn-activate" : "btn-deactivate"}`}
                    onClick={() => setStatusConfirm({ open: true, loan })}
                    title={isInactive ? "Reactivate loan" : "Mark as inactive"}
                  >
                    {isInactive ? <><FaToggleOff /> Activate</> : <><FaToggleOn /> Deactivate</>}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmData({ open: true, loanId: loan._id })}>
                    <FaTrash /> Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── STATUS TOGGLE CONFIRM ── */}
      {statusConfirm.open && statusConfirm.loan && (
        <ConfirmDialog
          title={statusConfirm.loan.status === "Active" ? "Mark as Inactive?" : "Reactivate Loan?"}
          message={
            statusConfirm.loan.status === "Active"
              ? `This will freeze interest calculation on today's date and grey out "${statusConfirm.loan.borrowerName}". You can reactivate it anytime.`
              : `This will reactivate "${statusConfirm.loan.borrowerName}" and resume interest calculation from today.`
          }
          confirmText={statusConfirm.loan.status === "Active" ? "Mark Inactive" : "Reactivate"}
          onCancel={() => setStatusConfirm({ open: false, loan: null })}
          onConfirm={() => handleToggleStatus(statusConfirm.loan)}
        />
      )}

      {/* ── DELETE CONFIRM ── */}
      {confirmData.open && (
        <ConfirmDialog
          title="Delete Loan?"
          message="This loan and all related transactions will be permanently deleted. This cannot be undone."
          confirmText="Delete"
          onCancel={() => setConfirmData({ open: false, loanId: null })}
          onConfirm={async () => {
            await handleDeleteLoan(confirmData.loanId);
            setConfirmData({ open: false, loanId: null });
          }}
        />
      )}

      {/* ── ADD / EDIT MODAL ── */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModals}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="loan_popup_cross_icon">
              <h3>{editLoanId ? "Edit Loan" : "New Loan"}</h3>
              <button className="modal-close-icon" onClick={closeModals} aria-label="Close">×</button>
            </div>
            <form className="popup-form" onSubmit={handleAddOrEditLoan}>
              <div className="form-group">
                <label className="input-label">Borrower Name</label>
                <input type="text" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="input-label">Phone Number</label>
                <input type="text" placeholder="Contact number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="input-label">Principal Amount (₹)</label>
                <input type="number" placeholder="0.00" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="input-label">Interest Rate (% per annum)</label>
                <input type="number" placeholder="0.00" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="input-label">Loan Start Date</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </div>
              <button type="submit">{editLoanId ? "Update Loan" : "Create Loan"}</button>
            </form>
            <button className="close-btn" onClick={closeModals}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── VIEW TRANSACTIONS ── */}
      {showViewModal && selectedLoan && (
        <TransactionHistory
          loan={selectedLoan}
          onClose={() => {
            setShowViewModal(false);
            setSelectedLoan(null);
            window.location.reload();
          }}
        />
      )}
      {/* ── INTEREST CALCULATOR MODAL ── */}
      {showCalc && <InterestCalculator onClose={() => setShowCalc(false)} />}

    </div>
  );
};

export default LoanList;
